import { useEffect, useRef, useState } from 'react';

/**
 * How many ad slots one page view may create.
 *
 * Every <sellwild-ad> insertion registers a fresh GAM slot in the widget and
 * pushes it onto a page-global `swpbjs.gamSlots` array that nothing ever
 * removes; each auction then calls `googletag.pubads().refresh(gamSlots)` on the
 * whole array, on a timer. On an infinite feed that means the cost of every
 * refresh cycle grows with how far the reader has scrolled, and GPT itself stops
 * cooperating past 30 slots per request ("SRA requests may include a maximum of
 * 30 ad slots ... the last N were ignored"). Slots past the cap were never
 * filled anyway, so refusing to create them costs no revenue.
 *
 * Since ./ad-slot-reaper destroys the slot of an ad that leaves the page, this
 * is a LIVE budget: an unmounted ad hands its claim back. A feed that parks its
 * off-screen rows (hooks/useWindowedRow) therefore keeps placing ads as deep as
 * the reader goes, with at most this many alive at once.
 */
export const MAX_AD_SLOTS_PER_PAGE = 20;

/**
 * Slots a page view holds back for its fixed placements — the top banner and
 * the sidebar panel.
 *
 * Those mount once, above the fold, and are the most valuable inventory on the
 * page. Without a reserve a long enough scroll spends the whole budget on the
 * feed before a client-side navigation's header banner ever asks.
 */
export const RESERVED_FIXED_AD_SLOTS = 2;

/**
 * How many ads one infinite feed may place.
 *
 * For the grid feed (legacy-listings), which never unmounts a tile while the
 * reader scrolls: every ad it places stays alive for the page view, so its
 * claims are never handed back, and past the cap `useAdSlot` refuses and
 * `AdFramework` renders null. Row feeds that park off-screen rows recycle
 * instead (see `useAdSlot`).
 *
 * That refusal used to be invisible to the feed, which had already committed the
 * grid cell AND dropped the listing that would have stood there — so past the
 * cap every ninth tile was a 250px hole with a listing missing behind it. A feed
 * asks `feedAdAllowed` FIRST and renders the listing when the answer is no.
 */
export const MAX_FEED_AD_SLOTS = MAX_AD_SLOTS_PER_PAGE - RESERVED_FIXED_AD_SLOTS;

/**
 * Whether a feed may place its nth ad, counting from 0.
 *
 * Deliberately a function of the ad's ORDINAL rather than of the live budget.
 * The answer has to be identical on every render: reading `granted` here would
 * let a re-render take an ad that is already on screen and swap it back to a
 * listing under the reader.
 */
export function feedAdAllowed(index: number, max: number = MAX_FEED_AD_SLOTS): boolean {
  return index >= 0 && index < max;
}

let granted = 0;
let pageKey: string | null = null;

/**
 * Begin the budget for a page view, identified by its path.
 *
 * Must be called DURING render, above the ads — not from an effect.
 * `useAdSlot` claims inside a state initializer, i.e. during render, while the
 * reset used to run in an `_app` effect that fires after commit. Navigating
 * after spending the budget therefore had the new page's first-wave ads (the
 * header banner, the three frames on a product page) claim against the OLD
 * exhausted count, get `false`, and render nothing at all — permanently, since
 * the answer is decided once per mounted ad. The budget then reset a moment
 * later, so only ads mounting afterwards got slots. Above-the-fold inventory
 * was exactly what was lost.
 *
 * Keyed by path rather than route: `/product/[id]` -> `/product/[id]` is the
 * most common navigation on the site and never changes `router.pathname`, so
 * the budget was never replenished while browsing products.
 *
 * Idempotent — calling it with the same key does nothing, so it is safe on
 * every render.
 */
export function beginAdPageView(key: string): void {
  if (key === pageKey) return;
  pageKey = key;
  granted = 0;
}

/** Start a new budget unconditionally. For tests and diagnostics. */
export function resetAdSlotBudget(): void {
  pageKey = null;
  granted = 0;
}

/** Slots handed out so far in this page view — for tests and diagnostics. */
export function grantedAdSlots(): number {
  return granted;
}

/**
 * Claim one slot from the page budget. Returns false once the page has spent
 * it, and the caller should then render nothing at all: a slot that exists but
 * goes unfilled still costs a refresh on every auction.
 *
 * Claimed through a state initializer so the answer is decided once per mounted
 * ad and never changes under it — and so an ad renders its slot on the first
 * pass rather than after a second render.
 *
 * Released on unmount: the reaper destroys the slot once the element is gone,
 * so a replacement claiming the space does not stack a second live slot on the
 * first. A release from a previous page view is ignored, since
 * `beginAdPageView` already started the count over.
 */
export function useAdSlot(max: number = MAX_AD_SLOTS_PER_PAGE): boolean {
  const [claimed] = useState(() => {
    if (granted >= max) return false;
    granted += 1;
    return true;
  });
  const [claimKey] = useState(() => pageKey);
  // StrictMode runs effects twice in development: mount, unmount, mount. The
  // second mount takes the claim back that the simulated unmount released.
  const releasedRef = useRef(false);

  useEffect(() => {
    if (!claimed) return;
    if (releasedRef.current && pageKey === claimKey) {
      granted += 1;
      releasedRef.current = false;
    }
    return () => {
      releasedRef.current = true;
      if (pageKey === claimKey && granted > 0) granted -= 1;
    };
  }, [claimed, claimKey]);

  return claimed;
}
